import { useCallback } from 'react';
import { viewports } from '@telus-uds/system-constants';
import useTheme from './useTheme';
import { getComponentTheme, mergeAppearances, resolveThemeTokens } from './utils';
const getResponsiveThemeTokens = function (_ref, tokensProp) {
  let {
    rules = [],
    tokens: defaultThemeTokens = {}
  } = _ref;
  let variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  let states = arguments.length > 3 ? arguments[3] : undefined;
  const appearances = mergeAppearances(variants, states);
  const tokensByViewport = Object.fromEntries(viewports.keys.map(viewport => [viewport, {
    ...defaultThemeTokens
  }]));

  // Go through each rule and collect them for the corresponding viewport if they apply
  rules.forEach(rule => {
    if (doesRuleApply(rule, appearances)) {
      // If the rule does not have a viewport specified, we collect it in all viewports
      let targetViewports = rule.if.viewport || viewports.keys;
      if (!Array.isArray(targetViewports)) {
        targetViewports = [targetViewports];
      }
      targetViewports.forEach(viewport => {
        tokensByViewport[viewport] = {
          ...tokensByViewport[viewport],
          ...rule.tokens
        };
      });
    }
  });
  Object.keys(tokensByViewport).forEach(viewport => {
    tokensByViewport[viewport] = resolveThemeTokens(tokensByViewport[viewport], appearances, tokensProp);
  });
  return tokensByViewport;
};
const doesRuleApply = (rule, appearances) => Object.entries(rule.if).every(condition => doesConditionApply(condition, appearances));
const doesConditionApply = (_ref2, appearances) => {
  let [key, value] = _ref2;
  if (key === 'viewport') {
    return true;
  }
  // use null rather than undefined so we can serialise the value in themes
  const appearanceValue = appearances[key] ?? null;
  return Array.isArray(value) ? value.includes(appearanceValue) : value === appearanceValue;
};

/**
 * @typedef {import('../utils/props/tokens.js').TokensSet} TokensSet
 * @typedef {import('../utils/props/tokens.js').TokensProp} TokensProp
 * @typedef {import('../utils/props/variantProp').AppearanceSet} AppearanceSet
 */

/**
 * Returns a memoised tokens getter function that gets responsive tokens for all viewports,
 * similar to calling useResponsiveThemeTokens but with the callback pattern of useThemeTokensCallback.
 *
 * Scenarios where `useResponsiveThemeTokensCallback` should be used:
 *
 * - Where responsive tokens are to be obtained from state that is accessible only in scopes like callbacks
 *   and render functions, where calling useResponsiveThemeTokens directly would be disallowed by React's hook rules.
 * - When using media query stylesheets and need to resolve tokens based on dynamic state (e.g., pressed, hovered)
 *   that changes at runtime.
 * - Passing a responsive tokens getter down via a child component's `tokens` prop, applying rules using the
 *   child component's current state.
 *
 * The function returned may be called with an object of state appearances to get an object
 * of tokens for each viewport, which can then be passed to createMediaQueryStyles.
 *
 * @example
 * // Resolving responsive tokens inside Pressable's style function, based on Pressable state
 * const PressMe = ({ tokens, variant, children }) => {
 *   const getResponsiveTokens = useResponsiveThemeTokensCallback('PressMe', tokens, variant)
 *   const getPressableStyle = ({ pressed }) => {
 *     const responsiveTokens = getResponsiveTokens({ pressed })
 *     const mediaQueryStyles = createMediaQueryStyles(responsiveTokens)
 *     return mediaQueryStyles
 *   }
 *   return <Pressable style={getPressableStyle}>{children}</Pressable>
 * }
 *
 * @example
 * // Setting the theme in a parent and resolving it in a child based on child's state
 * const MenuButton = ({ tokens, variant, ...buttonProps }) => {
 *   // Define what theme, variant etc we want in this component...
 *   const getResponsiveTokens = useResponsiveThemeTokensCallback('Button', tokens, variant)
 *   // ...resolve them in another component based on its state (e.g. press, hover...)
 *   return <ButtonBase tokens={getResponsiveTokens} accessibilityRole="menuitem" {...buttonProps} />
 * }
 *
 * @typedef {Object} ResponsiveObject
 * @property {TokensSet} xs
 * @property {TokensSet} sm
 * @property {TokensSet} md
 * @property {TokensSet} lg
 * @property {TokensSet} xl
 *
 * @param {string} componentName - the name as defined in the theme schema of the component whose theme is to be used
 * @param {TokensProp} [tokens] - every themed component should accept a `tokens` prop allowing theme tokens to be overridden
 * @param {AppearanceSet} [variants] - variants passed in as props that don't change dynamically
 * @returns {(states: AppearanceSet, tokenOverrides?: TokensProp) => ResponsiveObject}
 *    - callback function that returns an overridable responsive tokens object for current state. Only pass
 *      tokenOverrides in rare cases where tokens overrides are also generated outside hook scope.
 */
const useResponsiveThemeTokensCallback = function (componentName) {
  let tokens = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let variants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const theme = useTheme();
  const componentTheme = getComponentTheme(theme, componentName);
  const getResponsiveThemeTokensCallback = useCallback((states, tokenOverrides) => {
    const resolvedTokens = resolveThemeTokens(tokens, mergeAppearances(variants, states), tokenOverrides);
    return getResponsiveThemeTokens(componentTheme, resolvedTokens, variants, states);
  }, [componentTheme, tokens, variants]);
  return getResponsiveThemeTokensCallback;
};
export default useResponsiveThemeTokensCallback;